import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuditLogTab } from "@/components/AuditLogTab";
import AtecoMappingTab from "@/components/AtecoMappingTab";
import HistoricalImportTab from "@/components/HistoricalImportTab";
import { useAuth } from "@/contexts/AuthContext";
import { hasRole } from "@/lib/roles";
import GeneralSettingsTab from "@/components/GeneralSettingsTab";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const canMethod = hasRole(user, "resp_metodologia");
  const [tw, setTw] = useState([]);
  const [pw, setPw] = useState([]);
  const [qInds, setQInds] = useState([]); // Q-type indicators
  const [loading, setLoading] = useState(true);

  // Per-cluster benchmark state
  const [selectedCluster, setSelectedCluster] = useState("MAN-HEAVY");
  const [clusterBenches, setClusterBenches] = useState([]);
  const [benchLoading, setBenchLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, p, i] = await Promise.all([
        api.get("/topic-weights"),
        api.get("/pillar-weights"),
        api.get("/indicators"),
      ]);
      setTw(t.data); setPw(p.data);
      setQInds(i.data.filter((x) => x.type === "Q"));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadClusterBench = useCallback(async (cluster) => {
    setBenchLoading(true);
    try {
      const { data } = await api.get(`/benchmarks?cluster=${cluster}`);
      setClusterBenches(data);
    } finally { setBenchLoading(false); }
  }, []);
  useEffect(() => { if (selectedCluster) loadClusterBench(selectedCluster); }, [selectedCluster, loadClusterBench]);

  async function saveTopic(t, weight) {
    try { await api.put("/topic-weights", { topic_code: t.topic_code, weight }); toast.success("Peso topic aggiornato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  async function savePillar(c, weights) {
    try { await api.put("/pillar-weights", { cluster_code: c.cluster_code, ...weights }); toast.success("Pesi cluster aggiornati"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  async function saveClusterBench(indicator_code, p10_value, p90_value) {
    try {
      await api.put("/benchmarks", {
        indicator_code, cluster_code: selectedCluster,
        p10_value: Number(p10_value), p90_value: Number(p90_value),
      });
      toast.success("Benchmark cluster aggiornato");
      loadClusterBench(selectedCluster);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>;

  // Merge Q indicators with cluster benchmarks
  const benchMap = Object.fromEntries(clusterBenches.map((b) => [b.indicator_code, b]));

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">Impostazioni</div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Pesi & Benchmark</h1>
        <p className="text-slate-500 mt-1">Configura le formule di calcolo del rating ESG.</p>
        {!canMethod && (
          <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="settings-readonly-notice">
            Sola lettura: solo il Responsabile metodologia può modificare pesi, topic, benchmark e mapping ATECO.
          </div>
        )}
      </div>

      <Tabs defaultValue="pillar">
        <TabsList className="bg-slate-100 rounded-lg p-1">
          <TabsTrigger value="pillar" data-testid="tab-pillar">Cluster (pesi E/S/G)</TabsTrigger>
          <TabsTrigger value="topic" data-testid="tab-topic">Topic (21)</TabsTrigger>
          <TabsTrigger value="benchmark" data-testid="tab-benchmark">Benchmark P10/P90</TabsTrigger>
          <TabsTrigger value="ateco" data-testid="tab-ateco">Mapping ATECO</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="import" data-testid="tab-import">Import Storici</TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="general" data-testid="tab-general">Generali</TabsTrigger>
          )}
          <TabsTrigger value="audit" data-testid="tab-audit">Cronologia modifiche</TabsTrigger>
        </TabsList>

        <TabsContent value="pillar">
          <Card className="rounded-2xl border-slate-100 shadow-soft overflow-x-auto mt-4">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Cluster</th>
                  <th className="px-6 py-3">Etichetta</th>
                  <th className="px-6 py-3">Peso E</th>
                  <th className="px-6 py-3">Peso S</th>
                  <th className="px-6 py-3">Peso G</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pw.map((c) => <ClusterRow key={c.cluster_code} cluster={c} onSave={savePillar} canEdit={canMethod} />)}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="topic">
          <Card className="rounded-2xl border-slate-100 shadow-soft overflow-x-auto mt-4">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Topic</th>
                  <th className="px-6 py-3">Pilastro</th>
                  <th className="px-6 py-3">Etichetta</th>
                  <th className="px-6 py-3">Peso</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {tw.map((t) => <TopicRow key={t.topic_code} topic={t} onSave={saveTopic} canEdit={canMethod} />)}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="benchmark">
          <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden mt-4">
            <div className="p-5 border-b border-slate-100 flex items-center gap-4 flex-wrap">
              <div className="text-sm">
                <div className="font-semibold text-slate-900">Benchmark per cluster settoriale</div>
                <div className="text-slate-500 text-xs mt-0.5">
                  P10 = best practice (score 100). P90 = worst practice (score 0). Direzione &quot;neg&quot; = valori bassi sono migliori.
                </div>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cluster:</span>
                <Select value={selectedCluster} onValueChange={setSelectedCluster}>
                  <SelectTrigger data-testid="bench-cluster-select" className="h-9 rounded-lg w-56">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pw.map((c) => (
                      <SelectItem key={c.cluster_code} value={c.cluster_code}>
                        {c.cluster_code} — {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {benchLoading ? (
              <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-viva-500" /></div>
            ) : qInds.length === 0 ? (
              <div className="p-8 text-center text-slate-500">Nessun indicatore quantitativo.</div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="bg-slate-50/50">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-3">Indicatore</th>
                    <th className="px-6 py-3">Unità</th>
                    <th className="px-6 py-3">Dir.</th>
                    <th className="px-6 py-3">P10 (best)</th>
                    <th className="px-6 py-3">P90 (worst)</th>
                    <th className="px-6 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {qInds.map((ind) => {
                    const b = benchMap[ind.code];
                    return (
                      <ClusterBenchRow
                        key={`${selectedCluster}-${ind.code}`}
                        ind={ind}
                        bench={b}
                        onSave={(p10, p90) => saveClusterBench(ind.code, p10, p90)}
                        canEdit={canMethod}
                      />
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="ateco">
          <AtecoMappingTab isAdmin={isAdmin} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="import">
            <HistoricalImportTab />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="general">
            <GeneralSettingsTab />
          </TabsContent>
        )}

        <TabsContent value="audit">
          <AuditLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClusterRow({ cluster, onSave, canEdit }) {
  const [e, setE] = useState(cluster.weight_e);
  const [s, setS] = useState(cluster.weight_s);
  const [g, setG] = useState(cluster.weight_g);
  return (
    <tr data-testid={`cluster-row-${cluster.cluster_code}`}>
      <td className="px-6 py-3 font-medium text-slate-900">{cluster.cluster_code}</td>
      <td className="px-6 py-3 text-slate-600">{cluster.label}</td>
      <td className="px-6 py-3"><Input type="number" step="0.01" value={e} onChange={(ev) => setE(Number(ev.target.value))} className="h-9 w-24" /></td>
      <td className="px-6 py-3"><Input type="number" step="0.01" value={s} onChange={(ev) => setS(Number(ev.target.value))} className="h-9 w-24" /></td>
      <td className="px-6 py-3"><Input type="number" step="0.01" value={g} onChange={(ev) => setG(Number(ev.target.value))} className="h-9 w-24" /></td>
      <td className="px-6 py-3"><Button size="sm" disabled={!canEdit} onClick={() => onSave(cluster, { weight_e: e, weight_s: s, weight_g: g })} className="bg-viva-500 hover:bg-viva-600 text-white">Salva</Button></td>
    </tr>
  );
}

function TopicRow({ topic, onSave, canEdit }) {
  const [w, setW] = useState(topic.weight);
  return (
    <tr data-testid={`topic-row-${topic.topic_code}`}>
      <td className="px-6 py-3 font-medium text-slate-900">{topic.topic_code}</td>
      <td className="px-6 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100">{topic.pillar}</span></td>
      <td className="px-6 py-3 text-slate-600">{topic.label}</td>
      <td className="px-6 py-3"><Input type="number" step="0.01" value={w} onChange={(ev) => setW(Number(ev.target.value))} className="h-9 w-24" /></td>
      <td className="px-6 py-3"><Button size="sm" disabled={!canEdit} onClick={() => onSave(topic, w)} className="bg-viva-500 hover:bg-viva-600 text-white">Salva</Button></td>
    </tr>
  );
}

function ClusterBenchRow({ ind, bench, onSave, canEdit }) {
  const [p10, setP10] = useState(bench?.p10_value ?? "");
  const [p90, setP90] = useState(bench?.p90_value ?? "");
  // Reset state when bench changes (cluster switch)
  useEffect(() => {
    setP10(bench?.p10_value ?? "");
    setP90(bench?.p90_value ?? "");
  }, [bench?.p10_value, bench?.p90_value]);

  const dir = bench?.direction || ind.direction;
  return (
    <tr data-testid={`bench-row-${ind.code}`}>
      <td className="px-6 py-3">
        <div className="font-medium text-slate-900">{ind.code}</div>
        <div className="text-xs text-slate-500 max-w-md truncate">{ind.question_it}</div>
      </td>
      <td className="px-6 py-3 text-slate-600">{ind.unit || "—"}</td>
      <td className="px-6 py-3">
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${dir === "neg" ? "bg-amber-100 text-amber-700" : "bg-viva-100 text-viva-700"}`}>
          {dir}
        </span>
      </td>
      <td className="px-6 py-3">
        <Input type="number" step="any" value={p10} onChange={(ev) => setP10(ev.target.value)} className="h-9 w-28" data-testid={`bench-p10-${ind.code}`} />
      </td>
      <td className="px-6 py-3">
        <Input type="number" step="any" value={p90} onChange={(ev) => setP90(ev.target.value)} className="h-9 w-28" data-testid={`bench-p90-${ind.code}`} />
      </td>
      <td className="px-6 py-3">
        <Button size="sm" disabled={!canEdit} data-testid={`bench-save-${ind.code}`} onClick={() => onSave(p10, p90)} className="bg-viva-500 hover:bg-viva-600 text-white">Salva</Button>
      </td>
    </tr>
  );
}
